import { useCallback, useEffect, useState } from 'react'
import { jobsApi, type JobDetail, type JobHealth, type JobListStatus, type JobRow } from '../api/jobs'
import { userDirectoryApi, type DirectoryUser } from '../api/userDirectory'
import {
  Page,
  PageHeader,
  Tabs,
  Button,
  IconButton,
  AccessDenied,
  Callout,
  LoadingState,
  EmptyState,
  Card,
  DataTable,
  StatusPill,
  Dialog,
  TextField,
  Select,
  Pagination,
  KeyValueList,
  FormGrid,
} from '../components/ui'

/**
 * F-066 (TASK-0320, REQ-230-002, REQ-301-003): console dei job in background (solo Admin). Mostra i job in coda,
 * quelli falliti (dead letter) e quelli annullati. Su un job fallito si puo' riprovare, correggere il payload
 * (nasce un job nuovo, l'originale resta annullato e collegato), annullare o assegnare a un tecnico.
 * Il payload di un job esistente non viene mai modificato: la correzione e' sempre una copia.
 */
const PAGE_SIZE = 25

const TABS: { key: JobListStatus; label: string; empty: string }[] = [
  { key: 'pending', label: 'In coda', empty: 'Nessun job in coda' },
  { key: 'failed', label: 'Falliti', empty: 'Nessun job fallito' },
  { key: 'cancelled', label: 'Annullati', empty: 'Nessun job annullato' },
]

type Mode = 'view' | 'requeue' | 'correct' | 'cancel' | 'assign'

function formatDateTime(s: string | null): string {
  if (!s) return '—'
  return new Date(s).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

function truncate(s: string | null, n: number): string {
  if (!s) return '—'
  return s.length > n ? `${s.slice(0, n)}…` : s
}

/** "EqualCrm.Modules.Leads.Services.Bulk.LeadBulkOperationJob" -> "LeadBulkOperationJob". */
function shortType(jobType: string): string {
  const i = jobType.lastIndexOf('.')
  return i >= 0 ? jobType.slice(i + 1) : jobType
}

function isForbidden(e: unknown): boolean {
  const msg = e instanceof Error ? e.message : ''
  return msg.includes('403') || msg.toLowerCase().includes('forbidden') || msg.toLowerCase().includes('autorizzat')
}

const PRE_STYLE = {
  maxHeight: 300,
  overflow: 'auto',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  background: 'hsl(var(--md-surface-container-low))',
  border: '1px solid hsl(var(--md-outline-variant))',
} as const

function prettyPayload(payload: string): string {
  try {
    return JSON.stringify(JSON.parse(payload), null, 2)
  } catch {
    return payload
  }
}

export function JobsPage() {
  const [tab, setTab] = useState<JobListStatus>('failed')
  const [page, setPage] = useState(1)
  const [items, setItems] = useState<JobRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [unauthorized, setUnauthorized] = useState(false)
  const [health, setHealth] = useState<JobHealth | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [detail, setDetail] = useState<JobDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)
  const [mode, setMode] = useState<Mode>('view')

  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [payloadText, setPayloadText] = useState('')
  const [cancelReason, setCancelReason] = useState('')
  const [assignUser, setAssignUser] = useState('')
  const [assignNote, setAssignNote] = useState('')
  const [users, setUsers] = useState<DirectoryUser[]>([])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    setUnauthorized(false)
    try {
      const [list, h] = await Promise.all([jobsApi.list(tab, page, PAGE_SIZE), jobsApi.health()])
      setItems(list.items)
      setTotal(list.total)
      setHealth(h)
    } catch (e) {
      if (isForbidden(e)) setUnauthorized(true)
      else setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setLoading(false)
    }
  }, [tab, page])

  useEffect(() => {
    void load()
  }, [load])

  function changeTab(next: JobListStatus): void {
    setTab(next)
    setPage(1)
    setNotice(null)
  }

  async function openDetail(id: number): Promise<void> {
    setSelectedId(id)
    setDetail(null)
    setDetailError(null)
    setActionError(null)
    setMode('view')
    setDetailLoading(true)
    try {
      setDetail(await jobsApi.detail(id))
    } catch (e) {
      setDetailError(e instanceof Error && !e.message.includes('Not Found')
        ? e.message
        : 'Questo job non esiste più (forse è già stato eliminato dalla pulizia automatica).')
    } finally {
      setDetailLoading(false)
    }
  }

  function closeDetail(): void {
    if (busy) return
    setSelectedId(null)
    setDetail(null)
    setMode('view')
  }

  function startMode(next: Mode): void {
    setActionError(null)
    if (next === 'correct' && detail) setPayloadText(prettyPayload(detail.payload))
    if (next === 'cancel') setCancelReason('')
    if (next === 'assign') {
      setAssignUser(detail?.assignedToUserId ? String(detail.assignedToUserId) : '')
      setAssignNote(detail?.assignmentNote ?? '')
      if (users.length === 0) {
        userDirectoryApi.list({ limit: 200 }).then(setUsers).catch(() => setUsers([]))
      }
    }
    setMode(next)
  }

  /** Esegue un'azione sul job aperto: a successo chiude il dettaglio e ricarica l'elenco. */
  async function run(action: () => Promise<string | void>): Promise<void> {
    if (busy) return
    setBusy(true)
    setActionError(null)
    try {
      const message = await action()
      setNotice(message ?? 'Fatto.')
      setSelectedId(null)
      setDetail(null)
      setMode('view')
      await load()
    } catch (e) {
      if (isForbidden(e)) setUnauthorized(true)
      setActionError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setBusy(false)
    }
  }

  function submitCorrection(): void {
    if (!detail) return
    let parsed: unknown
    try {
      parsed = JSON.parse(payloadText)
    } catch (e) {
      setActionError(`JSON non valido: ${e instanceof Error ? e.message : 'errore di sintassi'}`)
      return
    }
    void run(async () => {
      const res = await jobsApi.correct(detail.id, parsed)
      return `Creato il job corretto n. ${res.newJobId}; l'originale n. ${detail.id} è stato annullato.`
    })
  }

  /** Invio del modulo: esegue l'azione della modalita' corrente (stesse chiamate dei vecchi pulsanti di conferma). */
  function submitMode(): void {
    if (!detail) return
    if (mode === 'requeue') {
      void run(async () => {
        await jobsApi.requeue(detail.id)
        return `Job #${detail.id} rimesso in coda.`
      })
    } else if (mode === 'correct') {
      submitCorrection()
    } else if (mode === 'cancel') {
      void run(async () => {
        await jobsApi.cancel(detail.id, cancelReason)
        return `Job #${detail.id} annullato.`
      })
    } else if (mode === 'assign') {
      void run(async () => {
        await jobsApi.assign(detail.id, assignUser ? Number(assignUser) : null, assignNote)
        return assignUser ? `Job #${detail.id} assegnato.` : `Assegnazione del job #${detail.id} rimossa.`
      })
    }
  }

  const canAct = detail !== null && (detail.status === 'Failed' || (detail.status === 'Pending' && !detail.claimed))
  const canCorrect = detail !== null && detail.status === 'Failed'

  return (
    <Page>
      <PageHeader
        title="Job in background"
        icon="work_history"
        actions={
          health ? (
            <div className="d-flex gap-2 md-typescale-body-small">
              <StatusPill tone="neutral">{health.pending} in coda</StatusPill>
              <StatusPill tone={health.deadLetterCount > 0 ? 'error' : 'success'}>
                {health.deadLetterCount} falliti
              </StatusPill>
              <StatusPill tone="neutral">{health.cancelledCount} annullati</StatusPill>
            </div>
          ) : undefined
        }
        tabs={
          !unauthorized ? (
            <Tabs
              aria-label="Stato dei job"
              items={TABS.map(t => ({ id: t.key, label: t.label }))}
              value={tab}
              onChange={id => changeTab(id as JobListStatus)}
            />
          ) : undefined
        }
      />

      {unauthorized && <AccessDenied requirement="il ruolo Admin" />}
      {!unauthorized && notice && (
        <Callout
          tone="success"
          action={<IconButton icon="close" size="sm" aria-label="Chiudi" onClick={() => setNotice(null)} />}
        >
          {notice}
        </Callout>
      )}
      {!unauthorized && error && <Callout tone="error">{error}</Callout>}

      {!unauthorized && (
        <Card variant="outlined" padding="none">
          {loading ? (
            <LoadingState />
          ) : items.length === 0 ? (
            <EmptyState icon="task_alt" title={TABS.find(t => t.key === tab)?.empty ?? ''} />
          ) : (
            <DataTable variant="plain">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Tipo</th>
                  <th>Pratica</th>
                  <th>Operatore</th>
                  <th>Tentativi</th>
                  <th>Ultimo errore</th>
                  <th>Creato il</th>
                  <th>{tab === 'cancelled' ? 'Annullato il' : 'Assegnato a'}</th>
                  <th className="text-end">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {items.map(row => (
                  <tr key={row.id}>
                    <td className="text-muted md-typescale-body-small md-num">{row.id}</td>
                    <td className="md-typescale-emphasized-body-medium" title={row.jobType}>{shortType(row.jobType)}</td>
                    <td className="text-muted md-typescale-body-small md-num">{row.contractId ? `#${row.contractId}` : '—'}</td>
                    <td className="text-muted md-typescale-body-small md-num">{row.operatorId ? `#${row.operatorId}` : '—'}</td>
                    <td className={`md-num ${row.attempts > 0 ? 'text-warning md-typescale-emphasized-body-medium' : 'text-muted'}`}>{row.attempts}</td>
                    <td className="text-muted md-typescale-body-small">{truncate(row.lastError, 80)}</td>
                    <td className="text-muted md-typescale-body-small text-nowrap">{formatDateTime(row.createdAt)}</td>
                    <td className="text-muted md-typescale-body-small text-nowrap">
                      {tab === 'cancelled'
                        ? formatDateTime(row.cancelledAt)
                        : row.assignedToUserId ? `Utente #${row.assignedToUserId}` : '—'}
                    </td>
                    <td className="text-end">
                      <Button variant="outlined" size="sm" onClick={() => void openDetail(row.id)}>
                        Dettagli
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          )}
          {totalPages > 1 && <Pagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} />}
        </Card>
      )}

      <Dialog
        as="form"
        onSubmit={(e) => { e.preventDefault(); submitMode() }}
        open={selectedId !== null}
        onClose={closeDetail}
        dismissible={!busy}
        title={`Job #${selectedId ?? ''}`}
        actions={
          mode === 'view' ? (
            <>
              <Button variant="text" onClick={closeDetail}>Chiudi</Button>
            </>
          ) : (
            <>
              <Button variant="text" disabled={busy} onClick={() => { setMode('view'); setActionError(null) }}>
                Indietro
              </Button>
              {detail && mode === 'requeue' && (
                <Button type="submit" variant="text" disabled={busy}>
                  {busy ? 'Invio...' : 'Conferma riprova'}
                </Button>
              )}
              {detail && mode === 'correct' && (
                <Button type="submit" variant="text" disabled={busy}>
                  {busy ? 'Invio...' : 'Crea job corretto'}
                </Button>
              )}
              {detail && mode === 'cancel' && (
                <Button type="submit" variant="text" tone="error" disabled={busy}>
                  {busy ? 'Invio...' : 'Conferma annullamento'}
                </Button>
              )}
              {detail && mode === 'assign' && (
                <Button type="submit" variant="text" disabled={busy}>
                  {busy ? 'Invio...' : 'Salva assegnazione'}
                </Button>
              )}
            </>
          )
        }
      >
        {detailLoading ? (
          <LoadingState />
        ) : detailError ? (
          <Callout tone="warning">{detailError}</Callout>
        ) : detail ? (
          <>
            {mode === 'view' && ((detail && canAct) || detail.status !== 'Processed') && (
              <div className="d-flex flex-wrap gap-2 align-items-center" role="group" aria-label="Azioni sul record">
                {canAct && canCorrect && (
                  <Button variant="tonal" onClick={() => startMode('requeue')}>Riprova</Button>
                )}
                {canAct && canCorrect && (
                  <Button variant="text" onClick={() => startMode('correct')}>Correggi…</Button>
                )}
                {canAct && (
                  <Button variant="text" onClick={() => startMode('cancel')}>Annulla job…</Button>
                )}
                {detail.status !== 'Processed' && (
                  <Button variant="text" onClick={() => startMode('assign')}>Assegna…</Button>
                )}
              </div>
            )}

            <KeyValueList
              items={[
                { label: 'Tipo', value: <span className="text-break">{detail.jobType}</span> },
                { label: 'Pratica', value: detail.contractId ? <>#{detail.contractId}</> : '—' },
                { label: 'Operatore', value: detail.operatorId ? <>#{detail.operatorId}</> : '—' },
                { label: 'Stato', value: <>{detail.status}{detail.claimed && detail.status === 'Pending' ? ' (in esecuzione)' : ''}</> },
                { label: 'Tentativi', value: detail.attempts },
                { label: 'Ultimo errore', value: <span className="text-break">{detail.lastError ?? '—'}</span> },
                { label: 'Creato il', value: formatDateTime(detail.createdAt) },
                ...(detail.failedAt ? [{ label: 'Fallito il', value: formatDateTime(detail.failedAt) }] : []),
                ...(detail.requestedByUserId ? [{ label: 'Richiesto da', value: <>Utente #{detail.requestedByUserId}</> }] : []),
                ...(detail.assignedToUserId ? [{
                  label: 'Assegnato a',
                  value: (
                    <>
                      Utente #{detail.assignedToUserId} il {formatDateTime(detail.assignedAt)}
                      {detail.assignmentNote ? ` — ${detail.assignmentNote}` : ''}
                    </>
                  ),
                }] : []),
                ...(detail.cancelledAt ? [{
                  label: 'Annullato',
                  value: (
                    <>
                      il {formatDateTime(detail.cancelledAt)} da utente #{detail.cancelledByUserId ?? '?'}
                      {detail.cancelReason ? ` — ${detail.cancelReason}` : ''}
                    </>
                  ),
                }] : []),
                ...(detail.supersededByJobId ? [{
                  label: 'Sostituito da',
                  value: (
                    <Button variant="text" size="sm" onClick={() => void openDetail(detail.supersededByJobId!)}>
                      job #{detail.supersededByJobId}
                    </Button>
                  ),
                }] : []),
              ]}
            />

            {mode === 'view' && (
              <div className="d-flex flex-column gap-1">
                <div className="md-typescale-label-medium text-muted">Payload</div>
                <pre className="rounded p-2 md-typescale-body-small mb-0" style={PRE_STYLE}>
                  {prettyPayload(detail.payload)}
                </pre>
              </div>
            )}

            {mode === 'requeue' && (
              <p className="mb-0">
                Rimetti in coda il job <strong>#{detail.id}</strong> come se non fosse mai stato eseguito: verrà
                rieseguito con lo stesso payload.
              </p>
            )}

            {mode === 'correct' && (
              <TextField
                label="Payload corretto (JSON)"
                multiline
                rows={12}
                spellCheck={false}
                className="font-monospace"
                supportingText="Il job originale non viene modificato: ne nasce uno nuovo con questo payload e l'originale resta annullato e collegato. Se il payload non è valido per questo tipo di job viene rifiutato."
                value={payloadText}
                onChange={e => setPayloadText(e.target.value)}
              />
            )}

            {mode === 'cancel' && (
              <TextField
                label="Motivo (facoltativo)"
                type="text"
                maxLength={500}
                supportingText="Un job annullato non verrà mai eseguito e non si può riattivare."
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
              />
            )}

            {mode === 'assign' && (
              <FormGrid>
                <Select label="Tecnico" value={assignUser} onChange={e => setAssignUser(e.target.value)}>
                  <option value="">Nessuno (togli l'assegnazione)</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </Select>
                <TextField
                  label="Nota"
                  type="text"
                  maxLength={500}
                  value={assignNote}
                  onChange={e => setAssignNote(e.target.value)}
                />
              </FormGrid>
            )}

            {actionError && <Callout tone="warning">{actionError}</Callout>}
          </>
        ) : null}
      </Dialog>
    </Page>
  )
}
